import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { LocationData, detectLocation, convertPrice, formatPrice, getCurrencySymbol } from '../utils/location';

interface LocationContextType {
  location: LocationData;
  setCurrency: (currency: string) => void;
  convertPrice: (priceUSD: number) => number;
  formatPrice: (price: number) => string;
  getCurrencySymbol: () => string;
  availableCurrencies: string[];
  isLoading: boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const AVAILABLE_CURRENCIES = ['USD', 'UGX', 'EUR', 'NGN', 'KES', 'ZAR'];

export function LocationProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<LocationData>({
    country: 'United States',
    countryCode: 'US',
    currency: 'USD',
    locale: 'en-US',
    region: 'North America',
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    detectLocation().then((detectedLocation) => {
      setLocation(detectedLocation);
      setIsLoading(false);
    });
  }, []);

  const setCurrency = (currency: string) => {
    setLocation((prev) => ({ ...prev, currency }));
  };

  const convertPriceLocal = (priceUSD: number) => {
    return convertPrice(priceUSD, location.currency);
  };

  const formatPriceLocal = (price: number) => {
    return formatPrice(price, location.currency);
  };

  const getCurrencySymbolLocal = () => {
    return getCurrencySymbol(location.currency);
  };

  return (
    <LocationContext.Provider
      value={{
        location,
        setCurrency,
        convertPrice: convertPriceLocal,
        formatPrice: formatPriceLocal,
        getCurrencySymbol: getCurrencySymbolLocal,
        availableCurrencies: AVAILABLE_CURRENCIES,
        isLoading,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}
